import { Button, ButtonProps, Spinner } from "@chakra-ui/react";
import { FC, useContext, useEffect, useState } from "react";
import writeXlsxFile from "write-excel-file";

import { AddIcon } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useAllThreediModelsByOwner } from "../../queries/threedimodels";

const excelFileSchemaCommon = [
  {
    column: "Model ID",
    value: (model: ThreediModel) => model.id,
    type: Number,
    align: "left",
    width: 10,
  },
  {
    column: "Name",
    value: (model: ThreediModel) => model.name,
    width: 80,
  },
  {
    column: "Last commit",
    value: (model: ThreediModel) =>
      model.revisionCommitDate ? new Date(model.revisionCommitDate) : null,
    type: Date,
    align: "left",
    width: 20,
  },
  {
    column: "User",
    value: (model: ThreediModel) => model.user,
    width: 15,
  },
  {
    column: "Storage",
    value: (model: ThreediModel) => model.storageSpaceHumanized,
    width: 10,
    align: "left",
  },
];

const excelFileSchemaNew = [
  ...excelFileSchemaCommon,
  {
    column: "Schematisation",
    value: (model: ThreediModel) => model.schematisationName,
    width: 80,
  },
  {
    column: "Revision",
    value: (model: ThreediModel) => model.revisionNumber,
    type: Number,
    align: "left",
    width: 10,
  },
];

const excelFileSchemaOld = [
  ...excelFileSchemaCommon,
  {
    column: "Repository",
    value: (model: ThreediModel) => model.repositorySlug,
    width: 80,
  },
];

const ModelsExcelButton: FC<ButtonProps> = ({ disabled, ...rest }) => {
  const { organisation } = useContext(OrganisationContext);
  const modelsResponse = useAllThreediModelsByOwner(organisation.uniqueId, true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const exportModels = async () => {
      const models = modelsResponse.isSuccess ? modelsResponse.data : [];
      const newModels = models.filter((model) => model.schematisationId && model.revisionId);
      const oldModels = models.filter((model) => !model.schematisationId || !model.revisionId);

      // @ts-ignore  Sadly write-excel-file's typescript doesn't handle the case of multiple schemas
      await writeXlsxFile([newModels, oldModels], {
        fileName: "Rana models overview.xlsx",
        schema: [excelFileSchemaNew, excelFileSchemaOld],
        fontFamily: "Montserrat", // Like the site
        dateFormat: "yyyy-mm-dd hh:mm",
        stickyRowsCount: 1, // Header
        sheets: ["Models", "Models.lizard.net"],
      });

      setExporting(false);
    };

    if (modelsResponse.isSuccess && exporting) exportModels();
  }, [exporting, modelsResponse.data, modelsResponse.isSuccess]);

  return (
    <Button
      variant="primary"
      title="Export to Excel file"
      leftIcon={<AddIcon boxSize={5} />}
      onClick={() => setExporting(true)}
      isDisabled={disabled || exporting || !modelsResponse.isSuccess}
      {...rest}
    >
      Export to Excel file
      {exporting ? <Spinner ml={2} size="xs" /> : null}
    </Button>
  );
};

export default ModelsExcelButton;
